import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import {
  ActivePopupMenu,
  EditIcon,
  IconButton,
  InputSearch,
  ListAltIcon,
  Modal,
  Paging,
  PdfIcon,
  SharedIcon,
  SpanInput,
  TrashIcon,
} from "../../components";
import { useErrorToast, usePromptConfirm } from "../../hooks";
import {
  activeSurvey,
  fetchAllSurveysByText,
  fetchFilteredSurveysByText,
  removeSurveysAndResearchText,
  surveysPages,
} from "../../stores/actions/management";
import { ReportManager } from "../ReportManager";
import EditorModal from "./EditorModal";
import ReportModal from "./ReportModal";
import ShareModal from "./ShareModal";
import * as Ui from "./styles";
import ReporterManagerIcon from "src/components/Icons/reporterManagerIcon";
import { ReportProvider } from "../ReportManager/context/ReportContext";

const textsByStatus = {
  DESATIVO: "INATIVO",
  NAO_PROCESSADO: "EM PROCESSAMENTO",
};

function SurveysManagement({
  /**
   * identidade do cliente
   */
  customerId,
  /**
   * open modal
   */
  isOpen = true,
  /**
   * propaga o evento de fechar o modal
   */
  onClose,
  /**
   * tema
   */
  theme,
  /**
   * outras propriedades implícitas
   */
  ...props
}) {
  // timeout id
  let timeoutId = -1;

  // from redux
  const { dispatch, surveysDatas, surveysPagesData } = props;

  // states
  // customerId, queryText, page, sortIndex, dirIndex, dispatch
  const [surveyId, setSurveyId] = useState({});
  const [clientId, setClientId] = useState({});
  const [searchText, setSearchText] = useState(null);
  const [surveysData, setSurveysData] = useState([]);
  const [isShow, setShow] = useState(false);
  const [isSharing, setSharing] = useState(false);
  const [isEditing, setEditing] = useState(false);
  const [isReportProcessing, setIsReportProcessing] = useState(false);
  const [isReportManagerVisible, setIsReportManagerVisible] = useState(false);
  // controla a pagina atual
  const [pageIndexInput, setPageIndexInput] = useState(0);

  // hooks
  const [checkError] = useErrorToast();
  const [confirmRemove, renderRemovingConfirmation] = usePromptConfirm();

  // ref
  const reportModalRef = useRef();

  // error effect
  useEffect(() => {
    checkError(surveysDatas.error);
  }, [checkError, surveysDatas.error]);
  // change surveys effect
  useEffect(() => {
    setSurveysData(surveysDatas.datas);
  }, [surveysDatas.datas]);

  // efeito de alteração do cliente
  useEffect(() => {
    if (isOpen) {
      setShow(isOpen);
      fetchAllSurveysByText(customerId, 0, dispatch);
      surveysPages(customerId, null, dispatch);
    }
  }, [isOpen, customerId, dispatch]);

  // manipula a pesquisa baseada no texto incluindo pelo usuário
  function handleChangeSearchTextWithPage(text, pageIndex) {
    clearTimeout(timeoutId);
    if (text && text.length > 0) {
      timeoutId = setTimeout(() => {
        setSearchText(text);
        fetchFilteredSurveysByText(customerId, text, pageIndex, dispatch);
        surveysPages(customerId, text, dispatch);
      }, 750);
    } else if (!text) {
      timeoutId = setTimeout(() => {
        setSearchText(null);
        fetchAllSurveysByText(customerId, pageIndex, dispatch);
        surveysPages(customerId, null, dispatch);
      }, 750);
    }
  }

  // manipula a pesquisa baseada no texto incluindo pelo usuário
  function handleChangeSearchText(text) {
    handleChangeSearchTextWithPage(text, 0);
  }

  // abre o modal de compartilhamento e seleciona a pesquisa a ser compartilhada
  function handleOpenShareModal(surveyItem) {
    setSurveyId(surveyItem.id);
    setSharing(true);
  }

  // abre o modal de edição da pesquisa
  function handleOpenEditorModal(surveyItem) {
    setSurveyId(surveyItem.id);
    setEditing(true);
  }

  // remove uma pesquisa e atualiza a lista de pesquisas
  function handleRemoveSurveyAndUpdateList(surveyItem) {
    confirmRemove(() => {
      removeSurveysAndResearchText(
        customerId,
        surveyItem.id,
        searchText,
        dispatch
      );
    });
  }

  // Fecha o modal de compartilhamento e limpa a pesquisa a ser compartilhada
  function handleCloseShareModal() {
    setSurveyId(null);
    setSharing(false);
  }

  // fechar o modal e acionar uma alteração da pesquisa atual
  function handleSaveShareModal() {
    handleCloseShareModal();
    handleChangeSearchText(searchText);
  }

  // Fecha o modal de edição e limpa a pesquisa a ser compartilhada
  function handleCloseEditorModal() {
    setSurveyId(null);
    setEditing(false);
  }

  // fecha o modal depois de salvo e atualiza a lista
  function handleSaveEditorModal() {
    handleCloseEditorModal();
    handleChangeSearchText(searchText);
  }

  function handleSelectActiveMenuItem(sId, sTitle, actived) {
    activeSurvey(customerId, sId, sTitle, actived, dispatch);
  }

  function handleClose() {
    setShow(false);
    if (onClose) onClose();
  }

  function handleSelectPage(pageIndex) {
    handleChangeSearchTextWithPage(searchText, pageIndex - 1);
    setPageIndexInput(pageIndex);
  }
  // Possibilita que o componente pai explicitamente defina a página do componente pagging
  function handlePageInput(pageIndex) {
    if (pageIndex > 0 && pageIndex <= surveysPagesData.data.pages) {
      setPageIndexInput(pageIndex);
      handleSelectPage(pageIndex);
    }
  }

  function renderFooter() {
    return (
      <Ui.TableFooter>
        <Ui.TablePaging>
          <Paging
            total={surveysPagesData.data ? surveysPagesData.data.pages : 0}
            onPrevious={(pageIndex) => handleSelectPage(pageIndex)}
            onNext={(pageIndex) => handleSelectPage(pageIndex)}
            onSelectedPage={(pageIndex) => handleSelectPage(pageIndex)}
            inputPage={pageIndexInput}
          />
        </Ui.TablePaging>
        <SpanInput title={"Ir para a página"} valueSelected={handlePageInput} />
      </Ui.TableFooter>
    );
  }

  /**
   * Funcao executada quando se deseja gerar
   * um relatorio
   */
  function handleGenerateReport(reportDataPayload) {
    reportModalRef.current.buildReportPayload(reportDataPayload);
  }

  /**
   * Controla a visualização do reportManager
   */
  function handleReportManager(survey) {
    setSurveyId(survey.id);
    setClientId(survey.cliente.id);
    setIsReportManagerVisible(true);
  }

  /**
   * * Controla a visualização do reportManager
   */
  function handleCloseReportManager() {
    setIsReportManagerVisible(false);
  }

  /**
   * Callback executado quando a geracao do payload do relatorio eh concluida
   */
  function onReportDone() {
    setIsReportProcessing(false);
  }

  return (
    <Modal
      hiddenOkButton
      showActions={!isReportProcessing}
      title={'Gerenciador de avaliações'}
      cancelBtnTitle={'Fechar'}
      isOpen={isShow}
      isLoading={false}
      onCancel={handleClose}
      width={"835px"}
      contentHeight={"550px"}
      borderTitle={"0 solid"}
      fontWeightTitle={"normal"}
      bodyDisplayType={"inline-block"}
      iconComponent={<ListAltIcon className="icon" color="#610082" size="15" />}
      // withLoadingSpinner={true}
      footerContent={renderFooter}
    >
      <ReportModal ref={reportModalRef} onFinish={onReportDone} />
      {isReportManagerVisible && (
        <ReportProvider>
          <ReportManager
            surveyId={surveyId}
            clientId={clientId}
            showModal={true}
            onClose={handleCloseReportManager}
            onExport={(preProcessedReportPayload) => handleGenerateReport(preProcessedReportPayload)}
          />
        </ReportProvider>
      )}
      <Ui.Container>
        {
          <div>
            {/*  painel de pesquisa */}
            <Ui.SearchPanel>
              <InputSearch
                textValue={searchText}
                onChangeText={(text) => handleChangeSearchText(text)}
              />
            </Ui.SearchPanel>
            {/*  painel da tabela */}
            <Ui.TablePanel>
              <Ui.TableContent>
                <table>
                  <thead>
                    <tr>
                      <th><Ui.BorderRight>id</Ui.BorderRight></th>
                      <th><Ui.BorderRight>cliente</Ui.BorderRight></th>
                      <th><Ui.BorderRight>avaliação</Ui.BorderRight></th>
                      <th><Ui.BorderRight>status</Ui.BorderRight></th>
                      <th> </th>
                      <th> </th>
                      <th> </th>
                    </tr>
                  </thead>
                  <tbody>
                    {surveysData &&
                      surveysData.map((item) => (
                        <tr
                          key={item.id}
                          className={
                            item.status === "NAO_PROCESSADO"
                              ? "inprogress"
                              : null
                          }
                        >
                          <td>{item.id}</td>
                          <td className="titulo">{item.cliente.nome}</td>
                          <td className="titulo">{item.titulo}</td>
                          <td
                            className={
                              item.ativo ? "status active" : "status inactive"
                            }
                          >
                            <ActivePopupMenu
                              isActived={item.ativo}
                              className={"popup-selector"}
                              onActive={(actived) =>
                                handleSelectActiveMenuItem(
                                  item.id,
                                  item.titulo,
                                  actived
                                )
                              }
                            >
                              {textsByStatus[item.status] || item.status}
                            </ActivePopupMenu>
                          </td>
                          <td>
                            {item.status !== "NAO_PROCESSADO" ? (
                              <IconButton
                                iconColor={theme.colors.surveysManagerIconColor}
                                iconSize={17}
                                iconComponent={ReporterManagerIcon}
                                title="Configurar relatório"
                                onClick={() => handleReportManager(item)}
                              />
                            ) : null}
                          </td>
                          <td>
                            {item.status !== "NAO_PROCESSADO" ? (
                              <IconButton
                                iconColor={theme.colors.surveysManagerIconColor}
                                iconStroke={
                                  theme.colors.surveysManagerIconColor
                                }
                                iconSize={17}
                                iconComponent={SharedIcon}
                                title="Compartilhar pesquisa"
                                onClick={() => handleOpenShareModal(item)}
                              />
                            ) : null}
                          </td>
                          <td>
                            {item.status !== "NAO_PROCESSADO" ? (
                              <IconButton
                                iconColor={theme.colors.surveysManagerIconColor}
                                iconSize={17}
                                iconComponent={EditIcon}
                                title="Editar"
                                onClick={() => handleOpenEditorModal(item)}
                              />
                            ) : null}
                          </td>
                          <td>
                            {item.status !== "NAO_PROCESSADO" ? (
                              <IconButton
                                iconColor={theme.colors.surveysManagerIconColor}
                                iconSize={17}
                                iconComponent={TrashIcon}
                                title="Excluir"
                                onClick={() =>
                                  handleRemoveSurveyAndUpdateList(item)
                                }
                              />
                            ) : null}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </Ui.TableContent>
            </Ui.TablePanel>
            {/* modal de compartilhamento */}
            {isSharing && (
              <ShareModal
                surveyId={surveyId}
                onClose={handleCloseShareModal}
                onSave={handleSaveShareModal}
              />
            )}
            {/* modal de compartilhamento */}
            {isEditing && (
              <EditorModal
                surveyId={surveyId}
                onClose={handleCloseEditorModal}
                onSave={handleSaveEditorModal}
                theme={theme}
              />
            )}
          </div>
        }
      </Ui.Container>
      {renderRemovingConfirmation(
        TrashIcon,
        "Deseja realmente excluir esta pesquisa?"
      )}
    </Modal>
  );
}

/**
 * mapeia a store para as propriedades que serão passadas para este
 * componente.
 *
 * Duas propriedades são extraídas e passadas para o componente:
 * surveys => obtido da pesquisa selecionada
 *
 * @param {*} state
 * @param {*} props
 */
const mapStateToProps = (state) => {
  // get state
  const surveysState = state.management.surveys;
  const surveysPagesState = state.management.surveysPages;
  const reportRawPayload = state.report.rawPayload;
  const reportProcessedPayload = state.report.processedPayload;
  // result
  return {
    reportRawPayload,
    reportProcessedPayload,
    surveysDatas: {
      running: surveysState.running,
      error: surveysState.error,
      datas: surveysState.payload,
    },
    surveysPagesData: {
      running: surveysPagesState.running,
      error: surveysPagesState.error,
      data: surveysPagesState.payload,
    },
  };
};

// create component (delegate) and export
export default connect(mapStateToProps, null, null, { forwardRef: false })(
  SurveysManagement
);
